import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const ACCOUNT_TYPES = [
  { value: "", label: "" },
  { value: "CAJA", label: "Caja" },
  { value: "CAJA_CHICA", label: "Caja chica" },
  { value: "FONDO_CAMBIO", label: "Fondo de cambio" },
  { value: "CAJA_POR_DEPOSITAR", label: "Caja por depositar" },
  { value: "BANCO", label: "Banco" },
  { value: "BANCO_DINERO_ELECTRONICO", label: "Dinero electrónico" },
  { value: "CUENTA_POR_COBRAR_TC", label: "Tarjeta por cobrar" },
  { value: "CUENTA_POR_PAGAR_TC", label: "Tarjeta por pagar" },
  { value: "CUENTA_POR_COBRAR_CLIENTES", label: "Clientes por cobrar" },
  { value: "CUENTA_POR_PAGAR_PROVEEDORES", label: "Proveedores por pagar" },
  { value: "DOCUMENTOS_POR_COBRAR", label: "Documentos por cobrar" },
  { value: "DOCUMENTOS_POR_PAGAR", label: "Documentos por pagar" },
  { value: "CHEQUES_POR_COBRAR", label: "Cheques por cobrar" },
  { value: "CHEQUES_POR_PAGAR", label: "Cheques por pagar" },
  { value: "ANTICIPO_PROVEEDORES", label: "Anticipo a proveedores" },
  { value: "ANTICIPO_CLIENTES", label: "Anticipo de clientes" },
  { value: "ANTICIPO_EMPLEADOS", label: "Anticipo a empleados" },
  { value: "PRESTAMO_EMPLEADOS", label: "Préstamo a empleados" },
  { value: "IVA_CREDITO", label: "IVA crédito tributario" },
  { value: "IVA_POR_PAGAR", label: "IVA por pagar" },
  { value: "IVA_PERCIBIDO", label: "IVA percibido" },
  { value: "ICE_POR_PAGAR", label: "ICE por pagar" },
  { value: "RETENCION_IVA_POR_COBRAR", label: "Retención IVA por cobrar" },
  { value: "RETENCION_RENTA_POR_COBRAR", label: "Retención renta por cobrar" },
  { value: "RETENCION_IVA_POR_PAGAR", label: "Retención IVA por pagar" },
  { value: "RETENCION_RENTA_POR_PAGAR", label: "Retención renta por pagar" },
  { value: "IMPUESTO_RENTA_POR_COBRAR", label: "Impuesto a la renta por cobrar" },
  { value: "IMPUESTO_RENTA_POR_PAGAR", label: "Impuesto a la renta por pagar" },
  { value: "INVENTARIO", label: "Inventario" },
  { value: "INVENTARIO_TRANSITO", label: "Inventario en tránsito" },
  { value: "INVENTARIO_AJUSTE", label: "Inventario por ajuste" },
  { value: "INVENTARIO_DANADO", label: "Inventario dañado o caducado" },
  { value: "PAGO_ANTICIPADO", label: "Pago anticipado" },
  { value: "ACTIVO_FIJO", label: "Activo fijo" },
  { value: "DEPRECIACION_ACUMULADA", label: "Depreciación acumulada" },
  { value: "INTANGIBLE", label: "Intangible" },
  { value: "OBLIGACION_LABORAL", label: "Obligación laboral" },
  { value: "CUENTA_POR_PAGAR_VARIAS", label: "Cuenta por pagar varias" },
  { value: "PRESTAMO_BANCARIO", label: "Préstamo bancario" },
  { value: "CAPITAL", label: "Capital" },
  { value: "RESULTADO", label: "Resultado" },
  { value: "RESERVA", label: "Reserva" },
  { value: "VENTA_PRODUCTOS", label: "Venta de productos" },
  { value: "VENTA_SERVICIOS", label: "Venta de servicios" },
  { value: "DESCUENTO_VENTAS", label: "Descuento en ventas" },
  { value: "DEVOLUCION_VENTAS", label: "Devolución en ventas" },
  { value: "NOTA_CREDITO_VENTAS", label: "Nota de crédito en ventas" },
  { value: "NOTA_DEBITO_VENTAS", label: "Nota de débito en ventas" },
  { value: "INGRESO_NO_OPERACIONAL", label: "Ingreso no operacional" },
  { value: "COSTO_VENTAS", label: "Costo de ventas" },
  { value: "COSTO_OPERATIVO", label: "Costo operativo" },
  { value: "GASTO_ADMINISTRATIVO", label: "Gasto administrativo" },
  { value: "GASTO_VENTAS", label: "Gasto de ventas" },
  { value: "GASTO_FINANCIERO", label: "Gasto financiero" },
  { value: "GASTO_INVENTARIO", label: "Gasto de inventario" },
  { value: "GASTO_NO_DEDUCIBLE", label: "Gasto no deducible" },
  { value: "DIFERENCIA_REDONDEO", label: "Diferencia por redondeo" },
  { value: "OTRO", label: "Otro" },
];

const currency = (n) =>
  new Intl.NumberFormat("es-EC", {
    style: "currency",
    currency: "USD",
  }).format(Number(n || 0));

const getDepth = (code) => Math.max(0, String(code || "").split(".").length - 1);

const typeLabel = (value) => {
  const current = ACCOUNT_TYPES.find((x) => x.value === value);
  if (current) return current.label;
  return String(value || "").replaceAll("_", " ").toLowerCase();
};

const AccountsView = ({
  accounts,
  loading,
  expandedSet,
  isAllExpanded,
  childrenByParent,
  hasChildren,
  toggleNode,
  toggleExpandAll,
  menuAnchorEl,
  openMenuFor,
  closeMenu,
  selectedAccount,
  canDeleteSelected,
  canEditSelected,
  canAddToSelected,
  openAddDialog,
  openEditDialog,
  openDeleteDialog,
  editorOpen,
  editorMode,
  formCode,
  formName,
  setFormName,
  formType,
  setFormType,
  formBalance,
  setFormBalance,
  balanceDisabled,
  typeDisabled,
  closeEditor,
  saveEditor,
  deleteOpen,
  closeDelete,
  confirmDelete,
  snackbarOpen,
  snackbarType,
  snackbarMessage,
  closeSnackbar,
  displayBalanceById,
}) => {
  const displayBalance = (id, fallback = 0) => {
    if (!displayBalanceById) return fallback;
    if (displayBalanceById.has(id)) return displayBalanceById.get(id);
    return fallback;
  };

  const renderRows = (parentId = null) => {
    const key = parentId ?? "__root__";
    const items = childrenByParent.get(key) || [];
    const rows = [];

    for (const a of items) {
      const depth = getDepth(a.code);
      const canExpand = hasChildren(a.id);
      const expanded = expandedSet.has(a.id);

      rows.push(
        <TableRow key={a.id} hover onClick={(e) => openMenuFor(e, a.id)} sx={{ cursor: "pointer" }}>
          <TableCell sx={{ width: 56, borderRight: "1px solid #e6e6e6" }}>
            {canExpand ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(a.id);
                }}
                sx={{ borderRadius: 1 }}
              >
                {expanded ? <RemoveIcon /> : <AddIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
          </TableCell>

          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: depth * 22 }} />
              <Typography sx={{ fontWeight: canExpand ? 900 : 600, color: "#111", display: "inline" }}>
                {a.code} {a.name}
              </Typography>
            </Box>
          </TableCell>

          <TableCell sx={{ whiteSpace: "nowrap", color: "#555" }}>{typeLabel(a.type)}</TableCell>

          <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 700 }}>
            {currency(displayBalance(a.id, a.balance))}
          </TableCell>
        </TableRow>
      );

      if (canExpand && expanded) rows.push(...renderRows(a.id));
    }

    return rows;
  };

  const helperBalanceText = useMemo(() => {
    if (editorMode === "add") return "Solo se usa como saldo inicial al crear una cuenta nueva.";
    return "El saldo se calcula automáticamente.";
  }, [editorMode]);

  const helperTypeText = useMemo(() => {
    if (typeDisabled) return "Las cuentas padre no usan tipo operativo.";
    return "";
  }, [typeDisabled]);

  return (
    <>
      <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Plan de Cuentas
            </Typography>

            <Button
              variant="outlined"
              onClick={toggleExpandAll}
              startIcon={isAllExpanded ? <RemoveIcon /> : <AddIcon />}
              sx={{ textTransform: "none" }}
            >
              {isAllExpanded ? "Contraer" : "Expandir"}
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 620, border: "1px solid #e6e6e6", borderRadius: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56, backgroundColor: "#f2f2f2" }}>
                    <Tooltip title={isAllExpanded ? "Contraer" : "Expandir"} arrow>
                      <IconButton size="small" onClick={toggleExpandAll} sx={{ borderRadius: 1, bgcolor: "white" }}>
                        {isAllExpanded ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800 }}>Cuenta</TableCell>

                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 240 }}>Tipo</TableCell>

                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, textAlign: "right", width: 170 }}>
                    Saldo
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 800, color: "#222" }}>No hay cuentas para mostrar</Typography>
                        <Typography sx={{ color: "#666", mt: 0.5 }}>
                          Verifica que el backend esté enviando el plan de cuentas.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  renderRows(null)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.18)",
            minWidth: 230,
          },
        }}
      >
        <MenuItem
          disabled={!canAddToSelected}
          onClick={() => {
            closeMenu();
            if (selectedAccount && canAddToSelected) openAddDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <AddIcon fontSize="small" />
          Agregar subcuenta
        </MenuItem>

        <MenuItem
          disabled={!canEditSelected}
          onClick={() => {
            closeMenu();
            if (selectedAccount && canEditSelected) openEditDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <EditIcon fontSize="small" />
          Modificar
        </MenuItem>

        <MenuItem
          disabled={!canDeleteSelected}
          onClick={() => {
            closeMenu();
            if (selectedAccount && canDeleteSelected) openDeleteDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <DeleteIcon fontSize="small" />
          Eliminar
        </MenuItem>

        <Divider />
      </Menu>

      <Dialog open={editorOpen} onClose={closeEditor} PaperProps={{ sx: { borderRadius: 2, width: 460 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {editorMode === "add" ? "Nueva cuenta" : "Modificar cuenta"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Código" value={formCode} fullWidth disabled />

          <TextField
            label="Nombre de la cuenta"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            autoFocus
          />

          <TextField
            select
            label="Tipo de cuenta"
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            fullWidth
            disabled={typeDisabled}
            helperText={helperTypeText}
          >
            {ACCOUNT_TYPES.map((t) => (
              <MenuItem key={t.value || "EMPTY"} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={editorMode === "add" ? "Saldo inicial" : "Saldo"}
            value={formBalance}
            onChange={(e) => setFormBalance(e.target.value)}
            fullWidth
            inputMode="decimal"
            disabled={balanceDisabled}
            helperText={helperBalanceText}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button variant="outlined" onClick={closeEditor} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={saveEditor} sx={{ textTransform: "none", backgroundColor: "#1565C0" }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={closeDelete} PaperProps={{ sx: { borderRadius: 2, width: 420 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>¿Eliminar cuenta?</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: "#444" }}>
            Solo se eliminará si no tiene movimientos, asientos, bancos, categorías ni subcuentas.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button variant="outlined" onClick={closeDelete} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>

          <Button variant="contained" color="error" onClick={confirmDelete} sx={{ textTransform: "none" }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={closeSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={closeSnackbar} severity={snackbarType} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AccountsView;